import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useDogs } from "@/context/DogsContext";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { DOG_TYPES } from "@/lib/dogTypes";
import { POPULAR_BREEDS } from "@/lib/breeds";
import { MAX_PHOTO_BYTES } from "@/lib/constants";
import { toast } from "sonner";

async function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

export default function AddDogDialog({ open, onOpenChange, editDog = null }) {
  const { refresh, selectDog } = useDogs();
  const { user } = useAuth();
  const hideRainbowBridge = !!user?.rainbow_bridge_hidden;
  const isEdit = !!editDog;
  // Edit mode skips the type-picker entirely; users can still change
  // dog_type from the deeper profile form on the Overview page.
  const [step, setStep] = useState(isEdit ? 2 : 1);
  const [dogType, setDogType] = useState(editDog?.dog_type || "show");
  const [name, setName] = useState(editDog?.name || "");
  const [breed, setBreed] = useState(editDog?.breed || "");
  const [birthday, setBirthday] = useState(editDog?.birthday || "");
  const [akc, setAkc] = useState(editDog?.akc_number || "");
  const [microchip, setMicrochip] = useState(editDog?.microchip || "");
  const [photo, setPhoto] = useState(editDog?.photo_url || "");
  // Rainbow Bridge — only surfaced in Edit mode. Users don't add already-passed
  // dogs from scratch; they toggle it when their real-life status changes.
  const [rainbowBridge, setRainbowBridge] = useState(!!editDog?.rainbow_bridge);
  const [passedOn, setPassedOn] = useState(editDog?.passed_on || "");
  const [memorial, setMemorial] = useState(editDog?.memorial || "");
  const [memorialPhoto, setMemorialPhoto] = useState(editDog?.memorial_photo_url || "");
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setStep(isEdit ? 2 : 1);
    setDogType(editDog?.dog_type || "show");
    setName(editDog?.name || "");
    setBreed(editDog?.breed || "");
    setBirthday(editDog?.birthday || "");
    setAkc(editDog?.akc_number || "");
    setMicrochip(editDog?.microchip || "");
    setPhoto(editDog?.photo_url || "");
    setRainbowBridge(!!editDog?.rainbow_bridge);
    setPassedOn(editDog?.passed_on || "");
    setMemorial(editDog?.memorial || "");
    setMemorialPhoto(editDog?.memorial_photo_url || "");
  };

  const close = () => { onOpenChange(false); setTimeout(reset, 200); };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!name) return;
    setSubmitting(true);
    try {
      const payload = {
        name, breed, birthday: birthday || null, akc_number: akc, microchip,
        photo_url: photo, dog_type: dogType,
        // When the Rainbow Bridge UI is hidden we DO NOT overwrite the
        // stored fields — that would lose the memorial data the moment a
        // user experiments with hiding it. Only re-send them when the user
        // is actively looking at the block.
        rainbow_bridge: isEdit && !hideRainbowBridge ? rainbowBridge : (editDog?.rainbow_bridge ?? false),
        passed_on: isEdit && !hideRainbowBridge ? (rainbowBridge ? passedOn : "") : (editDog?.passed_on || ""),
        memorial: isEdit && !hideRainbowBridge ? (rainbowBridge ? memorial : "") : (editDog?.memorial || ""),
        memorial_photo_url: isEdit && !hideRainbowBridge ? (rainbowBridge ? memorialPhoto : "") : (editDog?.memorial_photo_url || ""),
      };
      if (isEdit) {
        await api.put(`/dogs/${editDog.id}`, payload);
        toast.success("Dog updated");
      } else {
        const { data } = await api.post("/dogs", payload);
        toast.success("Dog added");
        selectDog(data.id);
      }
      await refresh();
      close();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || (isEdit ? "Could not save changes" : "Failed to add dog"));
    } finally {
      setSubmitting(false);
    }
  };

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_PHOTO_BYTES) {
      toast.error("Please choose a photo under 2 MB");
      return;
    }
    const data = await fileToDataUrl(file);
    setPhoto(data);
  };

  const onMemorialPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_PHOTO_BYTES) {
      toast.error("Please choose a photo under 2 MB");
      return;
    }
    const data = await fileToDataUrl(file);
    setMemorialPhoto(data);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid={isEdit ? "edit-dog-dialog" : "add-dog-dialog"}>
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight text-2xl">
            {isEdit ? "Edit dog" : (step === 1 ? "What kind of dog journey?" : "Add your dog")}
          </DialogTitle>
        </DialogHeader>

        {step === 1 ? (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Pick the mode that fits this dog best. You can change it later in the dog&apos;s profile.
            </p>
            <div className="grid grid-cols-1 gap-3">
              {DOG_TYPES.map((t) => {
                const Icon = t.icon;
                const active = dogType === t.value;
                return (
                  <button
                    key={t.value}
                    onClick={() => setDogType(t.value)}
                    data-testid={`dog-type-${t.value}`}
                    className={`flex items-start gap-3 p-4 rounded-md border text-left transition-all ${
                      active
                        ? "border-primary bg-secondary"
                        : "border-border bg-card hover:bg-muted"
                    }`}
                  >
                    <div className={`h-9 w-9 rounded-md flex items-center justify-center shrink-0 ${
                      active ? "bg-primary text-primary-foreground" : "bg-muted text-foreground"
                    }`}>
                      <Icon size={18} />
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium">{t.label}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{t.blurb}</p>
                    </div>
                  </button>
                );
              })}
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  if (isEdit) {
                    // Back in edit mode discards any unsaved mode pick so
                    // the tip on step 2 keeps showing the dog's persisted
                    // mode until the user explicitly hits "Use this mode".
                    setDogType(editDog?.dog_type || "show");
                    setStep(2);
                  } else {
                    close();
                  }
                }}
                data-testid="cancel-add-dog"
              >
                {isEdit ? "Back" : "Cancel"}
              </Button>
              <Button type="button" onClick={() => setStep(2)} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="dog-type-continue">
                {isEdit ? "Use this mode" : "Continue"}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="space-y-4">
            {/* Mode tip — only shown in Edit mode (Add mode hits step 1 first
                and already exposes the picker). Compact callout with the
                current mode's icon, label, and "what it unlocks" blurb; the
                Switch mode button hops back to step 1 where the picker lives. */}
            {isEdit && (() => {
              const current = DOG_TYPES.find((t) => t.value === dogType) || DOG_TYPES[0];
              const Icon = current.icon;
              return (
                <div
                  className="flex items-start gap-3 p-3 rounded-md border border-border bg-secondary/40"
                  data-testid="edit-dog-mode-tip"
                >
                  <div className="h-9 w-9 rounded-md bg-primary/10 text-primary flex items-center justify-center shrink-0">
                    <Icon size={16} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">Current mode</p>
                    <p className="font-medium text-sm" data-testid="edit-dog-mode-label">{current.label}</p>
                    <p className="text-xs text-muted-foreground leading-snug mt-0.5">{current.blurb}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setStep(1)}
                    data-testid="edit-dog-switch-mode"
                    className="shrink-0 text-xs font-medium text-primary hover:underline self-center px-2 py-1"
                  >
                    Switch mode
                  </button>
                </div>
              );
            })()}
            <div>
              <Label htmlFor="dog-name">Name</Label>
              <Input id="dog-name" data-testid="dog-name-input" value={name} onChange={(e) => setName(e.target.value)} required className="mt-1.5" autoFocus />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="dog-breed">Breed</Label>
                {(() => {
                  const value = breed || "";
                  const isCustom = !!value && !POPULAR_BREEDS.includes(value);
                  const selectVal = isCustom ? "__custom__" : value;
                  return (
                    <>
                      <select
                        id="dog-breed"
                        value={selectVal}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (v === "__custom__") {
                            setBreed(isCustom ? value : "");
                          } else {
                            setBreed(v);
                          }
                        }}
                        className="mt-1.5 w-full border border-input rounded-md bg-background px-3 py-2 text-sm h-10"
                        data-testid="dog-breed-select"
                      >
                        <option value="">Select a breed…</option>
                        {POPULAR_BREEDS.map((b) => (
                          <option key={b} value={b}>{b}</option>
                        ))}
                        <option value="__custom__">Other (type a custom breed)…</option>
                      </select>
                      {(isCustom || selectVal === "__custom__") && (
                        <Input
                          value={value}
                          onChange={(e) => setBreed(e.target.value)}
                          placeholder="Custom breed (e.g. Goldendoodle, mixed breed)"
                          className="mt-2"
                          data-testid="dog-breed-custom-input"
                          autoFocus={selectVal === "__custom__" && !value}
                        />
                      )}
                    </>
                  );
                })()}
              </div>
              <div>
                <Label htmlFor="dog-bday">Birthday / Adoption Day</Label>
                <Input id="dog-bday" data-testid="dog-birthday-input" type="date" value={birthday} onChange={(e) => setBirthday(e.target.value)} className="mt-1.5" />
              </div>
            </div>
            {dogType !== "pet" ? (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="dog-akc">AKC#, UKC#, IFC#, Other</Label>
                  <Input id="dog-akc" data-testid="dog-akc-input" value={akc} onChange={(e) => setAkc(e.target.value)} className="mt-1.5" />
                </div>
                <div>
                  <Label htmlFor="dog-chip">Microchip #</Label>
                  <Input id="dog-chip" data-testid="dog-microchip-input" value={microchip} onChange={(e) => setMicrochip(e.target.value)} className="mt-1.5" />
                </div>
              </div>
            ) : (
              <div>
                <Label htmlFor="dog-chip">Microchip #</Label>
                <Input id="dog-chip" data-testid="dog-microchip-input" value={microchip} onChange={(e) => setMicrochip(e.target.value)} className="mt-1.5" />
              </div>
            )}
            <div>
              <Label htmlFor="dog-photo">Profile photo</Label>
              <Input id="dog-photo" data-testid="dog-photo-input" type="file" accept="image/*" onChange={onPhoto} className="mt-1.5" />
              {photo ? <img src={photo} alt="preview" className="mt-3 h-24 w-24 object-cover rounded-md border" /> : null}
            </div>
            {isEdit && !hideRainbowBridge && (
              <div className="rounded-md border border-border bg-secondary/40 p-3 space-y-2" data-testid="rainbow-bridge-block">
                <label className="flex items-start gap-3 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={rainbowBridge}
                    onChange={(e) => setRainbowBridge(e.target.checked)}
                    className="mt-1 h-4 w-4 accent-primary"
                    data-testid="rainbow-bridge-toggle"
                  />
                  <div className="min-w-0">
                    <p className="text-sm font-medium">At the Rainbow Bridge</p>
                    <p className="text-xs text-muted-foreground leading-snug">
                      Keep {name || "your dog"} in your list so memories, photos, and records stay editable. A gentle badge will appear on the picker.
                    </p>
                  </div>
                </label>
                {rainbowBridge && (
                  <div className="space-y-3">
                    <div>
                      <Label htmlFor="dog-passed" className="text-xs uppercase tracking-wide text-muted-foreground">Passing date (optional)</Label>
                      <Input
                        id="dog-passed"
                        type="date"
                        value={passedOn}
                        onChange={(e) => setPassedOn(e.target.value)}
                        className="mt-1.5"
                        data-testid="rainbow-bridge-date"
                      />
                    </div>
                    <div>
                      <Label htmlFor="dog-memorial-photo" className="text-xs uppercase tracking-wide text-muted-foreground">Favorite photo (optional, under 2 MB)</Label>
                      <Input
                        id="dog-memorial-photo"
                        type="file"
                        accept="image/*"
                        onChange={onMemorialPhoto}
                        className="mt-1.5"
                        data-testid="rainbow-bridge-photo-input"
                      />
                      {memorialPhoto ? (
                        <div className="mt-3 flex items-center gap-3">
                          <img
                            src={memorialPhoto}
                            alt="Memorial preview"
                            className="h-24 w-24 object-cover rounded-md border border-primary/20"
                            data-testid="rainbow-bridge-photo-preview"
                          />
                          <button
                            type="button"
                            onClick={() => setMemorialPhoto("")}
                            className="text-xs text-muted-foreground hover:text-destructive underline"
                            data-testid="rainbow-bridge-photo-clear"
                          >
                            Remove photo
                          </button>
                        </div>
                      ) : null}
                    </div>
                    <div>
                      <Label htmlFor="dog-memorial" className="text-xs uppercase tracking-wide text-muted-foreground">A favorite memory (optional)</Label>
                      <Textarea
                        id="dog-memorial"
                        rows={3}
                        value={memorial}
                        onChange={(e) => setMemorial(e.target.value)}
                        placeholder="Zoomies on first snow · that one boop she always did · anything you want to remember."
                        className="mt-1.5"
                        maxLength={500}
                        data-testid="rainbow-bridge-memorial"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
            <DialogFooter className="flex justify-between">
              {isEdit ? (
                <Button type="button" variant="outline" onClick={close} data-testid="cancel-edit-dog">Cancel</Button>
              ) : (
                <Button type="button" variant="outline" onClick={() => setStep(1)} data-testid="back-add-dog">Back</Button>
              )}
              <Button type="submit" data-testid={isEdit ? "submit-edit-dog" : "submit-add-dog"} disabled={submitting} className="bg-primary text-primary-foreground hover:bg-primary/90">
                {submitting ? "Saving…" : (isEdit ? "Save changes" : "Save dog")}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

